import { z } from 'zod'
import { EmailSchema } from './user-validation.ts'

export const FeedbackTypeValues = [
	'page_issue',
	'feature_request',
	'missing_restaurant',
	'other',
] as const

export const FeedbackCategoryValues = [
	'wrong_recommendation',
	'outdated_info',
	'wrong_location',
	'broken_page',
	'other',
] as const

export const FeedbackStatusValues = [
	'new',
	'triaged',
	'planned',
	'closed',
	'spam',
] as const

const emptyStringToUndefined = (value: unknown) => {
	if (typeof value !== 'string') return value
	const trimmed = value.trim()
	return trimmed === '' ? undefined : trimmed
}

const OptionalTrimmedString = z.preprocess(
	emptyStringToUndefined,
	z.string().trim().optional(),
)

export const FeedbackTypeSchema = z.enum(FeedbackTypeValues)
export const FeedbackCategorySchema = z.enum(FeedbackCategoryValues)
export const FeedbackStatusSchema = z.enum(FeedbackStatusValues)

export const FeedbackSubmissionSchema = z
	.object({
		type: FeedbackTypeSchema,
		category: z.preprocess(
			emptyStringToUndefined,
			FeedbackCategorySchema.optional(),
		),
		message: z
			.string({ required_error: 'Please describe the issue or suggestion' })
			.trim()
			.min(20, 'Please add a bit more detail')
			.max(2000, 'Please keep feedback under 2000 characters'),
		email: z.preprocess(emptyStringToUndefined, EmailSchema.optional()),
		restaurantId: OptionalTrimmedString,
		pagePath: OptionalTrimmedString,
		pageUrl: z.preprocess(
			emptyStringToUndefined,
			z.string().trim().url('Page URL is invalid').optional(),
		),
	})
	.superRefine(({ category, type }, ctx) => {
		if (type === 'page_issue' && !category) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['category'],
				message: 'Choose what is wrong on the page',
			})
		}
	})

export function getFeedbackTypeLabel(type: string) {
	switch (type) {
		case 'page_issue':
			return 'Page issue'
		case 'feature_request':
			return 'Feature request'
		case 'missing_restaurant':
			return 'Missing restaurant'
		case 'other':
			return 'Other'
		default:
			return type
	}
}

export function getFeedbackCategoryLabel(category: string) {
	switch (category) {
		case 'wrong_recommendation':
			return 'Wrong recommendation'
		case 'outdated_info':
			return 'Outdated info'
		case 'wrong_location':
			return 'Wrong location or map'
		case 'broken_page':
			return 'Broken page'
		case 'other':
			return 'Other'
		default:
			return category
	}
}

export function getFeedbackStatusLabel(status: string) {
	switch (status) {
		case 'new':
			return 'New'
		case 'triaged':
			return 'Triaged'
		case 'planned':
			return 'Planned'
		case 'closed':
			return 'Closed'
		case 'spam':
			return 'Spam'
		default:
			return status
	}
}

export type FeedbackSubmissionInput = z.infer<typeof FeedbackSubmissionSchema>
